import { useState, useEffect } from 'react';
import { io } from 'socket.io-client';

// const socket = io('https://api.trapskunk.com', {
//   transports: ['websocket'],
//   reconnection: true,
//   reconnectionAttempts: Infinity,
//   reconnectionDelay: 1000,
// });

const socket = io('https://api.solskunk.com', {
  transports: ['websocket'],
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 1000,
});

const RATE_LIMIT_WINDOW = 10000; // 15 seconds in milliseconds

export function ChatInput({ onMessageSent }) {
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessageTime, setLastMessageTime] = useState(0);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  useEffect(() => {
    let cooldownInterval;
    if (cooldownRemaining > 0) {
      cooldownInterval = setInterval(() => {
        const remaining = Math.max(0, RATE_LIMIT_WINDOW - (Date.now() - lastMessageTime));
        setCooldownRemaining(remaining);
      }, 1000);
    }
    return () => clearInterval(cooldownInterval);
  }, [lastMessageTime, cooldownRemaining]);

  useEffect(() => {
    const handleConnect = () => {
      console.log('Socket connected');
      setIsConnected(true);
    };

    const handleDisconnect = () => {
      console.log('Socket disconnected');
      setIsConnected(false);
      setIsLoading(false);
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleDisconnect);

    socket.on('chat:response', (response) => {
      console.log('Received response:', response);
      setIsLoading(false);
      if (onMessageSent) {
        onMessageSent(response);
      }
    });

    socket.on('chat:error', (error) => {
      console.error('Chat error:', error);
      setIsLoading(false);
      if (error.error === 'RATE_LIMITED') {
        const now = Date.now();
        setLastMessageTime(now - (RATE_LIMIT_WINDOW - error.message.match(/\d+/)[0] * 1000));
        setCooldownRemaining(RATE_LIMIT_WINDOW - (now - lastMessageTime));
      }
    });

    // Set initial state
    setIsConnected(socket.connected);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleDisconnect);
      socket.off('chat:response');
      socket.off('chat:error');
    };
  }, [onMessageSent, lastMessageTime]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() || isLoading || !isConnected) return;

    const now = Date.now();
    const timeSinceLastMessage = now - lastMessageTime;
    
    if (timeSinceLastMessage < RATE_LIMIT_WINDOW) {
      const remaining = Math.ceil((RATE_LIMIT_WINDOW - timeSinceLastMessage) / 1000);
      setCooldownRemaining(RATE_LIMIT_WINDOW - timeSinceLastMessage);
      return;
    }

    setIsLoading(true);
    setLastMessageTime(now);
    setCooldownRemaining(RATE_LIMIT_WINDOW);
    
    try {
      socket.emit('chat:message', message.trim());
      setMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      setIsLoading(false);
    }
  };

  const isDisabled = !isConnected || isLoading || cooldownRemaining > 0;
  const buttonText = cooldownRemaining > 0 
    ? `Wait ${Math.ceil(cooldownRemaining / 1000)}s` 
    : isLoading ? "Thinking..." : "Talk";

  return (
    <div className="w-full max-w-2xl mx-auto px-4">
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative flex items-center bg-black/20 backdrop-blur-lg border border-white/10 rounded-2xl shadow-xl overflow-hidden">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={isConnected ? "Ask the skunk anything..." : "Waiting for Skunk..."}
            disabled={!isConnected}
            className="w-full px-6 py-4 bg-transparent text-white placeholder-white/50 focus:outline-none disabled:opacity-50 pr-32"
          />
          <button
            type="submit"
            disabled={isDisabled}
            className="absolute right-2 px-6 py-2 bg-purple-600/90 hover:bg-purple-700/90 disabled:bg-purple-800/50 disabled:opacity-50 text-white rounded-xl transition-colors backdrop-blur-sm"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>{buttonText}</span>
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                <span>{buttonText}</span>
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
} 